// Rule assembly + engine mapping for the risk gate (WS-E). The per-engine
// pattern TABLES stay in `core/engines/engineDefinitions.ts` as the data
// source; this module compiles them into one ordered rule list per engine and
// owns the engine-id → grammar-dialect / pattern-table mapping (CK-E §2).

import { getDangerousPatterns } from '../../core/engines';
import type { DatabaseEngineId, DangerousQueryPatterns, RiskReasonClass } from '../../core/engines';
import type { ParseOptions, SqlEngine } from '../sql-grammar/model';

export interface RiskRule {
  readonly id: string;
  readonly pattern: RegExp;
  readonly severity: 'high' | 'low';
  readonly reasonClass: RiskReasonClass;
  /** True for `dangerousWithoutWhere` rules — a match only counts when the
   *  slice has no WHERE clause (or only a tautological one). */
  readonly requiresNoRowFilter: boolean;
}

/** App engine id → grammar dialect. kusto is absent on purpose — it never
 *  goes through the SQL splitter (KQL branch in riskGate.ts). */
export const GRAMMAR_ENGINE_BY_ID: Partial<Record<DatabaseEngineId, SqlEngine>> = {
  mssql: 'mssql',
  pgsql: 'postgres',
  mysql: 'mysql',
  oracle: 'oracle',
  sqlite: 'sqlite',
  mock: 'mssql',
};

/** App engine id → pattern-table source. mock aliases the FULL MSSQL table
 *  for e2e realism (CK-E §2 — an intentional, fixture-recorded divergence:
 *  mock gains sp_configure/xp_cmdshell/DBCC and the WS-E rule additions). */
const PATTERN_SOURCE_BY_ID: Partial<Record<DatabaseEngineId, DatabaseEngineId>> = {
  mock: 'mssql',
};

export function patternsFor(engineId: DatabaseEngineId): DangerousQueryPatterns | undefined {
  return getDangerousPatterns(PATTERN_SOURCE_BY_ID[engineId] ?? engineId);
}

/** Compile the three table categories into ONE ordered list — the order IS
 *  the CK-E "stable table order" tie-breaker: alwaysDangerous, then
 *  dangerousWithoutWhere, then engineSpecific, each in declared order. */
export function compileRules(patterns: DangerousQueryPatterns): RiskRule[] {
  return [
    ...patterns.alwaysDangerous.map((p) => ({
      id: p.id,
      pattern: p.pattern,
      severity: p.severity,
      reasonClass: p.reasonClass,
      requiresNoRowFilter: false,
    })),
    ...patterns.dangerousWithoutWhere.map((p) => ({
      id: p.id,
      pattern: p.pattern,
      severity: 'high' as const,
      reasonClass: 'unbounded-dml' as const,
      requiresNoRowFilter: true,
    })),
    ...patterns.engineSpecific.map((p) => ({
      id: p.id,
      pattern: p.pattern,
      severity: 'high' as const,
      reasonClass: p.reasonClass,
      requiresNoRowFilter: false,
    })),
  ];
}

/** Default ParseOptions per grammar dialect (mirrors the adoption seams —
 *  the gate takes raw text + engine id, no session options). */
export function parseOptionsFor(engine: SqlEngine): ParseOptions {
  switch (engine) {
    case 'mssql':
      return { engine, quotedIdentifier: true, identifierFolding: 'case-insensitive' };
    case 'postgres':
      return { engine, standardConformingStrings: true, identifierFolding: 'lower' };
    case 'mysql':
      return { engine, ansiQuotes: false, mysqlVersionGate: 'ge80', currentDelimiter: ';', identifierFolding: 'case-insensitive' };
    case 'oracle':
      return { engine, identifierFolding: 'upper' };
    case 'sqlite':
      return { engine, identifierFolding: 'case-insensitive' };
  }
}

/** Dialog prose per reason class — the DangerousQueryDialog derives its
 *  title/summary copy from substrings of these exact strings (legacy parity). */
const REASON_PROSE: Record<RiskReasonClass, string> = {
  'parse-ambiguous': 'Could not parse query structure - please review before executing',
  destructive: 'This query will permanently modify or delete data',
  'routine-drop': 'This drops a database routine or view',
  'unbounded-dml': 'This statement affects all rows because it has no WHERE clause',
  'system-command': 'This is a system-level command that could affect server stability',
  'unsupported-management': 'This is a system-level command that could affect server stability',
};

export function proseFor(reasonClass: RiskReasonClass): string {
  return REASON_PROSE[reasonClass];
}

/** Tautology variants get their own truthful prose — the dialog's substring
 *  matching falls through to its generic high-severity copy for these. */
export const TAUTOLOGY_PROSE =
  'This statement affects all rows because its WHERE clause is always true (1=1)';
export const MERGE_TAUTOLOGY_PROSE =
  'This statement affects all rows because its MERGE ON condition is always true (1=1)';
